import Logger from '@/logger';
import { type IPluginCommand, type IPluginDocumentation } from '@/plugin';
import soft from '@/softconfig';
import { openDb } from '@/db/connection';
import { runPluginMigrations } from '@/db/migrate';
import { importNedbIfNeeded } from '@/db/nedbImport';
import * as pluginSchema from '@/db/pluginSchema';
import { and, count, eq } from 'drizzle-orm';
import {
  drizzle,
  type BetterSQLite3Database,
} from 'drizzle-orm/better-sqlite3';
import fs from 'node:fs';
import path from 'node:path';
import type Player from './player';
import { Plugin } from './plugin/interface';
import RpcPlugin from './plugin/plugin_jsonrpc_stdio';
import NodeVmPlugin from './plugin/plugin_node_safe';
import NodePlugin from './plugin/plugin_node_unsafe';
import type Omegga from './server';

export interface IPluginJSON {
  formatVersion: number;
  omeggaVersion: string;
  emitConfig?: string;
  dependencies?: {
    [pluginName: string]: { optional?: boolean; repo?: string } | string;
  };
  loadPriority?: number;
  loadBefore?: string[];
  loadAfter?: string[];
}

// TODO: move doc.json to this file (maybe)
// TODO: cleaner plugin error messages

// key-value storage for a plugin
export class PluginStorage {
  private db: BetterSQLite3Database;
  plugin: Plugin;
  name: string;

  constructor(db: BetterSQLite3Database, plugin: Plugin) {
    this.db = db;
    this.plugin = plugin;
    this.name = plugin.getName();
  }

  // get the initial config values
  getDefaultConfig() {
    const doc = this.plugin.getDocumentation();
    if (!doc) return {};
    if (!doc.config) return {};
    const config: Record<string, unknown> = {};
    for (const k in doc.config) {
      config[k] = doc.config[k].default;
    }
    return config;
  }

  // set the config field for this plugin
  async setConfig(value: unknown) {
    this.db
      .insert(pluginSchema.pluginConfig)
      .values({ plugin: this.name, value: value as Record<string, unknown> })
      .onConflictDoUpdate({
        target: pluginSchema.pluginConfig.plugin,
        set: { value: value as Record<string, unknown> },
      })
      .run();
  }

  // get the config for this plugin
  async getConfig(): Promise<Record<string, unknown> | null> {
    const row = this.db
      .select()
      .from(pluginSchema.pluginConfig)
      .where(eq(pluginSchema.pluginConfig.plugin, this.name))
      .get();
    if (!row) return null;
    return row.value;
  }

  /** Wipes the configs for the plugin. */
  async wipeConfig() {
    this.db
      .delete(pluginSchema.pluginConfig)
      .where(eq(pluginSchema.pluginConfig.plugin, this.name))
      .run();
  }

  /** Initializes the configs for the plugin into the database and sets default configs. */
  async init() {
    const defaultConf = this.getDefaultConfig();
    const config = await this.getConfig();
    await this.setConfig({
      // use default config
      ...defaultConf,
      // override with actual config values
      ...(config ? config : {}),
    });
  }

  // get a stored object
  async get<T>(key: string) {
    if (typeof key !== 'string' || key.length === 0) return;
    const row = this.db
      .select()
      .from(pluginSchema.pluginStore)
      .where(
        and(
          eq(pluginSchema.pluginStore.plugin, this.name),
          eq(pluginSchema.pluginStore.key, key),
        ),
      )
      .get();
    if (!row) return null;
    return row.value as T;
  }

  // delete a stored object
  async delete(key: string) {
    if (typeof key !== 'string' || key.length === 0) return;
    this.db
      .delete(pluginSchema.pluginStore)
      .where(
        and(
          eq(pluginSchema.pluginStore.plugin, this.name),
          eq(pluginSchema.pluginStore.key, key),
        ),
      )
      .run();
  }

  // clear all stored values
  async wipe() {
    this.db
      .delete(pluginSchema.pluginStore)
      .where(eq(pluginSchema.pluginStore.plugin, this.name))
      .run();
  }

  // count number of objects in store
  async count() {
    const result = this.db
      .select({ count: count() })
      .from(pluginSchema.pluginStore)
      .where(eq(pluginSchema.pluginStore.plugin, this.name))
      .get();
    return result?.count ?? 0;
  }

  // get keys of all objects in store
  async keys() {
    const rows = this.db
      .select({ key: pluginSchema.pluginStore.key })
      .from(pluginSchema.pluginStore)
      .where(eq(pluginSchema.pluginStore.plugin, this.name))
      .all();
    return rows.map(r => r.key);
  }

  // set a stored value
  async set<T = unknown>(key: string, value: T) {
    if (typeof key !== 'string' || key.length === 0) return;
    // the value column is NOT NULL and drizzle's json mode passes JS null/
    // undefined through as SQL NULL (and values that serialize to undefined,
    // e.g. functions); treat all of these as a delete, matching get()'s null
    // return for absent keys (NeDB silently stored such values)
    if (value == null || JSON.stringify(value) === undefined)
      return this.delete(key);
    this.db
      .insert(pluginSchema.pluginStore)
      .values({ plugin: this.name, key, value })
      .onConflictDoUpdate({
        target: [pluginSchema.pluginStore.plugin, pluginSchema.pluginStore.key],
        set: { value },
      })
      .run();
  }
}

/*
  Plugin Loader and Scanner
    Allows omegga to scan in plugins and load them
*/
export class PluginLoader {
  path: string;
  dataPath: string;
  // undefined when constructed from the CLI (config commands)
  omegga: Omegga | undefined;
  pluginDb: BetterSQLite3Database;
  formats: (typeof Plugin)[];
  plugins: Plugin[];

  commands: Record<string, IPluginCommand & { _plugin: Plugin }> = {};
  documentation: Record<string, IPluginDocumentation & { _plugin: Plugin }> =
    {};

  constructor(workDir: string, omegga?: Omegga) {
    this.path = path.join(workDir, soft.PLUGIN_PATH);
    this.omegga = omegga;

    this.dataPath = path.join(workDir, soft.DATA_PATH);
    const sqlite = openDb(path.join(this.dataPath, soft.PLUGINS_DB));
    this.pluginDb = drizzle(sqlite);
    runPluginMigrations(this.pluginDb);

    this.plugins = [];

    Logger.verbose('Loading plugin formats');
    this.formats = [RpcPlugin, NodeVmPlugin, NodePlugin];
    Logger.verbose('Found plugin formats:', this.formats);

    if (omegga) {
      // soon to be deprecated !help
      omegga.on('chatcmd:help', this.showHelp.bind(this));

      // use /plugins to get help
      omegga.on('cmd:plugins', this.showHelp.bind(this));
    }
  }

  // determine if this command is a command on the plugin
  isCommand(cmd: string) {
    return cmd === 'plugins' || this.plugins.some(p => p.isCommand(cmd));
  }

  // unload and load all installed plugins
  async reload() {
    let ok = true;
    Logger.verbose('Reloading plugins');
    for (const p of this.plugins) {
      try {
        // unload the plugin if it's loaded
        if (p.isLoaded()) await p.unload();

        // load the plugin if it successfully unloaded
        if (!p.isLoaded()) {
          // only load it if it is enabled
          if (p.isEnabled()) {
            Logger.verbose(
              'Loading plugin',
              (p.constructor as typeof Plugin).getFormat(),
              p.getName().underline,
            );
            ok = (await p.load()) || ok;
          }
        } else {
          Logger.errorp(
            'Did not successfully unload plugin',
            p.getName().brightRed.underline,
          );
          ok = false;
        }
      } catch (err) {
        Logger.errorp(
          'Error loading plugin',
          p.getName().brightRed.underline,
          err,
        );
        ok = false;
      }
    }
    Logger.verbose('Finished reloading plugins');
    return ok;
  }

  // stop all plugins from running
  async unload() {
    Logger.verbose('Unloading plugins');
    let ok = true;
    // potentually use Promise.all
    for (const p of this.plugins) {
      try {
        // unload the plugin if it's loaded
        if (p.isLoaded()) {
          if (!(await p.unload())) {
            Logger.errorp(
              'Could not unloading plugin',
              p.getName().brightRed.underline,
            );
            ok = false;
            continue;
          } else {
            Logger.verbose('Unloaded', p.getName().underline);
          }
        }
      } catch {
        Logger.errorp(
          'Error unloading plugin',
          p.getName().brightRed.underline,
        );
        ok = false;
      }
    }
    return ok;
  }

  /** Scans a plugin at the specified directory and create a Plugin object. */
  async scanPlugin(dir: string): Promise<Plugin | undefined> {
    Logger.verbose('Scanning plugin', dir.underline);

    // legacy NeDB plugin data must be imported before storage.init() writes
    // defaults, even when the webserver (the other import trigger) is disabled
    await importNedbIfNeeded(this.dataPath);

    if (!fs.existsSync(dir)) {
      Logger.errorp('Plugin directory does not exist', dir.brightRed.underline);
      return;
    }
    if (!fs.statSync(dir).isDirectory()) {
      Logger.errorp('Plugin must be a directory', dir.brightRed.underline);
      return;
    }

    // find a plugin format that can load in this plugin
    const PluginFormat = this.formats.find(f => f.canLoad(dir));

    // let users know if there's a missing plugin format
    if (!PluginFormat) {
      Logger.errorp('Missing plugin format for', dir);
      return;
    }
    try {
      // create the plugin format
      const plugin = PluginFormat && new PluginFormat(dir, this.omegga);
      if (!plugin.getDocumentation()) {
        Logger.errorp('Missing/invalid plugin documentation for', dir);
        return;
      }

      // create its storage
      const storage = new PluginStorage(this.pluginDb, plugin);
      await storage.init();

      // load the storage in
      plugin.setStorage(storage);

      // if there is a plugin format, create the plugin instance (but don't load yet)
      return plugin;
    } catch (e) {
      // if a plugin format fails to load, prevent omegga from dying
      Logger.errorp(
        'Error loading plugin',
        dir.brightRed.underline,
        PluginFormat,
        e,
      );
    }
  }

  static calculateLoadOrder(unsortedPlugins: Plugin[]) {
    if (!unsortedPlugins.length) {
      return [];
    }

    // create a DAG of plugins based on dependencies, loadBefore, and loadAfter
    type DependencyGraphNode = {
      plugin: Plugin;
      inDegree: number;
      behind: string[];
    };
    type DependencyGraph = Record<string, DependencyGraphNode>;

    const depGraph = unsortedPlugins.reduce((graph, plugin) => {
      const name = plugin.getName();
      const {
        dependencies = {},
        loadAfter = [],
        loadBefore = [],
      } = plugin.pluginConfig ?? {};

      //create a node in case it doesn't exist
      graph[name] ??= {} as DependencyGraphNode;
      graph[name].plugin ??= plugin;
      graph[name].behind ??= [];
      graph[name].inDegree ??= 0;

      // add edges for everything ahead of name
      for (const depName of [...loadAfter, ...Object.keys(dependencies)]) {
        graph[name].behind.push(depName);

        //create a node and set an inDegree in case it doesn't exist
        graph[depName] ??= {} as DependencyGraphNode;
        graph[depName].inDegree ??= 0;

        graph[depName].inDegree++;
      }

      // add edges for everything behind name
      for (const depName of [...loadBefore]) {
        //create a node and a behind array in case it doesn't exist
        graph[depName] ??= {} as DependencyGraphNode;
        graph[depName].behind ??= [];

        graph[depName].behind.push(name);

        graph[name].inDegree++;
      }

      return graph;
    }, {} as DependencyGraph);

    // find all nodes with no incoming edges to start processing
    let zeros: string[] = Object.entries(depGraph).reduce<string[]>(
      (arr, [name, { inDegree }]) => {
        if (inDegree === 0) {
          arr.push(name);
        }
        return arr;
      },
      [],
    );

    // execute a topological sort, grouping nodes where the order doesn't matter, in the order that *does* matter
    const result: string[][] = [];
    while (zeros.length) {
      // these nodes can be in any order
      result.unshift([...zeros]);

      const nextZero = [];

      for (const node of zeros) {
        for (const neighbor of depGraph[node].behind) {
          depGraph[neighbor].inDegree--;
          if (depGraph[neighbor].inDegree === 0) {
            nextZero.push(neighbor);
          }
        }
      }

      zeros = nextZero;
    }

    if (Object.values(depGraph).some(({ inDegree }) => inDegree > 0)) {
      Logger.verbose(
        'Cyclic or missing dependencies detected in plugins. Could not resolve load order.',
      );
      return [];
    }

    return result
      .map(interchangeablePlugins =>
        interchangeablePlugins
          .map(name => depGraph[name].plugin)
          // alphabetical order for the sub groups where order doesn't matter so its deterministic
          .sort((a, b) => a.getName().localeCompare(b.getName())),
      )
      .flat()
      .sort((a, b) => {
        const A = a.pluginConfig?.loadPriority;
        const B = b.pluginConfig?.loadPriority;
        // sort the result by load priority so that
        // a defined loadPriority that is negative comes before undefined (lower is earlier)
        // and a defined loadPriority that is positive comes after undefined (higher is later)
        if (A == null && B == null) return 0;
        if (A == null) return B != null && B <= 0 ? 1 : -1;
        if (B == null) return A <= 0 ? -1 : 1;
        return A - B;
      });
  }

  /** Scans plugin directory for plugins and builds their documentation. */
  async scan() {
    Logger.verbose('Scanning plugin directory');
    // plugin directory doesn't exist
    if (!fs.existsSync(this.path)) {
      Logger.verbose('Plugin directory is missing');
      return false;
    }

    // make sure there are no plugins running
    if (this.plugins.some(p => p.isLoaded())) {
      Logger.errorp('Cannot re-scan plugins while a plugin is loaded');
      return false;
    }

    // find all directories in the plugin path
    const unsortedPlugins = (
      await Promise.all(
        fs
          .readdirSync(this.path)
          .map(dir => path.join(this.path, dir)) // convert from local paths
          .map(async dir => this.scanPlugin(dir)),
      )
    )
      // remove plugins without formats
      .filter(p => p !== undefined);

    this.plugins = PluginLoader.calculateLoadOrder(unsortedPlugins);

    Logger.verbose('Finished scanning plugin directory');
    Logger.verbose(
      `Found ${this.plugins.length} plugins.Loading in this order: `,
      this.plugins.map(p => p.getName()),
    );

    this.buildDocumentation();

    return true;
  }

  // show helptext
  showHelp(player: string | Player, ...args: string[]) {
    // send the message to the player (only registered when a server exists)
    const send = (msg: string) => this.omegga?.whisper(player, msg);

    // available commands and documentation from the plugin system
    const commands = this.commands ?? {};
    const docs = this.documentation ?? {};

    const splitHelper = (objs: string[]) => {
      const lines = [];
      while (objs.length > 0) {
        const [item] = objs.splice(0, 1);
        if (!lines.length || lines[lines.length - 1].length + item.length > 150)
          lines.push(item);
        else lines[lines.length - 1] += ', ' + item;
      }
      return lines;
    };

    // no arguments
    if (!args.length) {
      send(
        '"Use <code>/plugins plugin</>, <code>/plugins !command</>, <code>/plugins /command</> for more information"',
      );
      const plugins = Object.keys(docs).map(
        d =>
          `<color=\\"${
            docs[d]._plugin.isLoaded() ? 'aaffaa' : 'aaaaaa'
          }\\">${d}</>`,
      );
      if (!plugins.length) {
        send('"<b>No Installed Plugins</>"');
      } else {
        const lines = splitHelper(plugins);
        send(`"<b>Installed Plugins</>: ${lines[0]}"`);
        for (let i = 1; i < lines.length; i++) send(`"${lines[i]}"`);
      }

      // plugin or command argument
    } else if (args.length > 0) {
      const target = args.join(' ').toLowerCase();

      // argument is a plugin; render description, author, and commands
      if (target in docs) {
        const doc = docs[target];
        const desc = doc.description || 'no description';
        const color = doc._plugin.isLoaded() ? 'aaffaa' : 'aaaaaa';
        send(
          `"<b>Plugin</> <code><color=\\"${color}\\">${doc.name}</></>: ${desc}"`,
        );

        if (doc.author)
          send(`"<b>Author</>: <color=\\"c4d7f5\\"><b>${doc.author}</></>"`);

        if (doc.commands && doc.commands.length > 0) {
          const lines = splitHelper(
            doc.commands.map(c => `<code>${c.name}</>`),
          );
          send(`"<b>Commands</>: ${lines[0]}"`);
          for (let i = 1; i < lines.length; i++) send(`"${lines[i]}"`);
        }

        // argument is a command
      } else if (target in commands) {
        const cmd = commands[target];
        const desc = cmd.description || 'no description';
        const example = cmd.example || 'no example';
        const color = cmd._plugin.isLoaded() ? 'aaffaa' : 'aaaaaa';
        send(
          `"<b>Command</> <code><color=\\"${color}\\">${cmd.name}</></>: ${desc}"`,
        );
        send(`"<b>Example</>: <code>${example}</>"`);
        if (cmd.args && cmd.args.length > 0) {
          send('"<b>Arguments</>:"');
          for (const arg of cmd.args) {
            const desc = arg.description || 'no description';
            send(
              `"- <code>${arg.name}</>${
                arg.required ? ' (required)' : ''
              }: ${desc}"`,
            );
          }
        } else {
          send('"<b>Arguments</>: None"');
        }

        // user takes the helptext literally
      } else if (
        args[0] === '!command' ||
        args[0] === '/command' ||
        args[0] === 'plugin'
      ) {
        send(
          '"Use <code>/plugins [name of plugin]</> or <code>/plugins [name of !command or /command]</> for more help for the respective plugin or command"',
        );

        // argument is not found
      } else {
        send('"Could not find that command or plugin"');
      }

      // too many arguments
    } else {
      send(
        '"Use <code>/plugins</> to list plugins and <code>/plugins plugin</> or <code>!help !command or /command</> for more information"',
      );
    }
  }

  // generate documentation from loaded plugins
  buildDocumentation() {
    this.documentation = {};
    this.commands = {};
    for (const plugin of this.plugins) {
      // make sure this plugin has docs
      const doc = JSON.parse(JSON.stringify(plugin.getDocumentation()));
      if (!doc) continue;
      const name = plugin.getName();
      if (!name) continue;

      // add the documentation into a dictionary
      this.documentation[name.toLowerCase()] = doc;
      doc._plugin = plugin;

      // add all the commands into a dictionary
      if (doc.commands) {
        for (const cmd of doc.commands) {
          this.commands[cmd.name.toLowerCase()] = cmd;
          cmd._plugin = plugin;
        }
      }
    }
  }
}
